import { checkinSymptomFieldKeys } from "./symptom-entry";

// TES-984 (Ö-266) — which weekly check-in (F-04) answers the doctor's monthly
// note may read. Emelie 2026-09-28: relevance follows the F-04 field
// classification DOCTOR / SHARED / COACH, not only the risk flags.
//
// The classification did not exist as data: the template carries no
// audience per field. So it is DERIVED here from the template's own sections
// (weekly-checkin-content.v4/v5) and Emelie's answer, and written down once:
//
//   section "signals" ("Signaler och besvär")        → DOCTOR, every field
//   week_dose_adherence(+_what) ("Veckans status")   → DOCTOR — her list has
//       "dose adherence when activated"; the note reads it only in a week she
//       reported a deviation (MONTHLY_WEEKLY_ACTIVATION below)
//   week_wellbeing, week_weight(+_measured_on)       → SHARED
//   week_focus ("Fokus och stöd")                    → SHARED
//   week_support, week_note, week_goal_mode, week_goal → COACH, never read
//
// Emelie: "Never: WEEK_SUPPORT, WEEK_NOTE, coach feedback, coach notes and
// agreements." The last three are not F-04 answers at all (they live in the
// coach's own tables), and the monthly note never queries those tables.
//
// This is the DOCTOR NOTE's classification. The coach's trend draws its own
// line (apps/api/src/clinician/checkin-trend.ts) for a different question —
// what a coach may see — and is not changed by this file.
//
// An unknown key (a field from a version nobody here has seen) has no class
// and is NOT read: failing closed keeps a future coach question out of the
// record until someone classifies it.

export const F04_FIELD_CLASSES = ["DOCTOR", "SHARED", "COACH"] as const;
export type F04FieldClass = (typeof F04_FIELD_CLASSES)[number];

export const F04_FIELD_CLASS: Readonly<Record<string, F04FieldClass>> = {
  // v4/v5 "Signaler och besvär"
  week_new_or_changed_symptoms: "DOCTOR",
  week_symptom_types: "DOCTOR",
  week_symptom_timing: "DOCTOR",
  week_symptom_severity: "DOCTOR",
  week_symptom_impact: "DOCTOR",
  week_urgent_screen: "DOCTOR",
  week_drive_mood_signal: "DOCTOR",
  week_drive_what: "DOCTOR",
  week_drive_since: "DOCTOR",
  week_drive_impact: "DOCTOR",
  // v7 (TES-1251) — the per-symptom questions under the same gate: symptom
  // details, the doctor's (A-01).
  ...Object.fromEntries(
    checkinSymptomFieldKeys("week").map((key) => [key, "DOCTOR" as const]),
  ),
  // v4/v5 "Veckans status", the medical part of it
  week_dose_adherence: "DOCTOR",
  week_dose_adherence_what: "DOCTOR",
  // SHARED — allowed in the doctor's note
  week_wellbeing: "SHARED",
  week_weight: "SHARED",
  // v8 (TES-1449) — the weight's own measurement date: the same measurement,
  // so the same class, and the note can date the weight (as F-05 v8 does).
  week_weight_measured_on: "SHARED",
  week_focus: "SHARED",
  // COACH — never in the doctor's note
  week_support: "COACH",
  week_note: "COACH",
  week_goal_mode: "COACH",
  week_goal: "COACH",
  // v1/v2 keys, still on older submissions: the side-effect block was the
  // medical part of that generation; the note was to the coach.
  sideEffects: "DOCTOR",
  sideEffectSeverity: "DOCTOR",
  note: "COACH",
};

/** The field's class, or null for a key nobody has classified (not read). */
export function f04FieldClass(key: string): F04FieldClass | null {
  return F04_FIELD_CLASS[key] ?? null;
}

/** DOCTOR and SHARED answers may reach the monthly note; COACH and unknown never. */
export function isMonthlyNoteF04Field(key: string): boolean {
  const c = f04FieldClass(key);
  return c === "DOCTOR" || c === "SHARED";
}

// "When activated": a gate whose answer only matters to the note when she
// reported something. The value listed is the one that means "nothing to
// report"; any other answer activates the block. The complaints gate
// (week_new_or_changed_symptoms) is deliberately NOT here: Emelie asks for
// her "Nej" on new or changed complaints as well.
export const MONTHLY_WEEKLY_ACTIVATION: Readonly<Record<string, string>> = {
  week_dose_adherence: "ja",
  week_drive_mood_signal: "nej",
};
